import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { generateClient } from "@aws-amplify/api";
import { upsertSubject as _upsertSubject } from "@/graphql/mutations";
import { getSubjects as _getSubjects } from "@/graphql/queries";
import ErrorModal from "@/ErrorModal";
import Breadcrumb from "@/Breadcrumb";
import useFormFields from "@/hooks/useFormFields";
import SubjectSections from "./all-sections/SubjectSections";
import {
  ENUM_MAPS,
  normalizeEnum,
  sanitizeMedicalSections,
} from "./all-sections/enumMaps";

const client = generateClient();
const purple = "#2E2868";

const fetchSubject = async ({ id }) => {
  const res = await client.graphql({
    query: _getSubjects,
    variables: { id, integrations: { clinicalConductor: true } },
  });

  const data = res.data.getSubjects.subjects?.[0];
  if (!data) throw new Error("Subject not found");

  const flat = { subjects: {} };
  Object.keys(data).forEach((key) => {
    if (key.startsWith("subject_")) {
      if (Array.isArray(data[key])) {
        flat[key] = _.cloneDeep(data[key]);
        flat[key].forEach((e, i) => (e.__orig = data[key][i]));
      } else {
        flat[key] = data[key];
      }
    } else flat.subjects[key] = data[key];
  });

  return flat;
};

const upsertSubject = ({ input, input_delete }) =>
  client
    .graphql({
      query: _upsertSubject,
      variables: { input, input_delete },
    })
    .then((res) => res.data.upsertSubject);

export default function SubjectCallViewOnly({
  createLabel = "Create",
  updateLabel = "Save",
  links = [],
  onCancel,
  onSave,
}) {
  const {
    origModel,
    model,
    setModel,
    changeModel,
    deleteModel,
    setErrors,
    hasValue,
    id,
    isCreate,
    sanitizeObject,
    add_array,
    remove_array,
    set_array,
    get_array,
  } = useFormFields();

  const initializedRef = useRef(false);

  const [isLoaded, setIsLoaded] = useState(isCreate);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  /* LOAD */
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (isCreate) {
      setModel({ subjects: {}, subject_phone_numbers: [{}] });
      setIsLoaded(true);
      return;
    }

    fetchSubject({ id })
      .then((data) => {
        origModel.current = _.cloneDeep(data);
        setModel(_.cloneDeep(data));
      })
      .catch((err) => setErrorMessage(err.message))
      .finally(() => setIsLoaded(true));
  }, [id, isCreate, setModel]);

  /* VIEW helpers */
  const formHelpers = useMemo(
    () => ({
      model,
      changeModel,
      add_array,
      remove_array,
      set_array,
      get_array,

      setValue: ({ key, value }) => {
        if (Array.isArray(value)) {
          changeModel.current[key] = value;
        } else {
          changeModel.current[key] = {
            ...(changeModel.current[key] || {}),
            ...value,
          };
        }
      },
    }),
    [model, changeModel, add_array, remove_array, set_array, get_array]
  );

  const callView = useMemo(
    () => (
      <SubjectSections
        id={id}
        model={model}
        changeModel={changeModel}
        isCreate={isCreate}
        form={formHelpers}
      />
    ),
    [id, model, isCreate, formHelpers]
  );

  /* SAVE LOGIC */
  const handleSave = async () => {
    try {
      setIsSaving(true);

      const source = changeModel.current;

      // Note: We use || null to ensure values are sent even if empty string
      const subjects = {
        first_name: source.subjects?.first_name || null,
        last_name: source.subjects?.last_name || null,
        date_of_birth: source.subjects?.date_of_birth || null,
        height_in: source.subjects?.height_in || null,
        weight_lb: source.subjects?.weight_lb || null,
        lifestyle_notes: source.subjects?.lifestyle_notes || null,
        alcohol_use_frequency: normalizeEnum(
          source.subjects?.alcohol_use_frequency,
          ENUM_MAPS.alcohol_use_frequency
        ),
        diet_type: normalizeEnum(
          source.subjects?.diet_type,
          ENUM_MAPS.diet_type
        ),
        ethnicity: normalizeEnum(
          source.subjects?.ethnicity,
          ENUM_MAPS.ethnicity
        ),
        gender: normalizeEnum(source.subjects?.gender, ENUM_MAPS.gender),
        race: normalizeEnum(source.subjects?.race, ENUM_MAPS.race),
        smoker_status: normalizeEnum(
          source.subjects?.smoker_status,
          ENUM_MAPS.smoker_status
        ),
        stress_level: normalizeEnum(
          source.subjects?.stress_level,
          ENUM_MAPS.stress_level
        ),
        title: normalizeEnum(source.subjects?.title, ENUM_MAPS.title),
        ...(id ? { id } : {}),
      };

      /* PAYLOAD INIT */
      const payload = {
        subjects,
        subject_phone_numbers: source.subject_phone_numbers
          ? Object.values(source.subject_phone_numbers)
          : [],
        subject_emails: source.subject_emails
          ? Object.values(source.subject_emails)
          : [],
        subject_notes: source.subject_notes
          ? Object.values(source.subject_notes).map((n) => ({
              note: n.note,
              created_by: n.created_by,
              ...(n.id ? { id: n.id } : {}),
            }))
          : [],
      };

      /* Multiple Studies Logic */
      const enrollments = source.subject_enrollment_status
        ? Object.values(source.subject_enrollment_status).filter(
            (e) => e.study_id && e.enrollment_status
          )
        : [];

      if (enrollments.length) {
        payload.subject_enrollment_status = enrollments.map((e) => ({
          study_id: e.study_id,
          enrollment_status: e.enrollment_status,
          ...(e.id ? { id: e.id } : {}),
        }));
      }

      /* Independent Recruitment Sources Logic */
      const recruitments = source.subject_recruitment_sources
        ? Object.values(source.subject_recruitment_sources).filter(
            (e) => e.recruitment_id
          )
        : [];

      if (recruitments.length) {
        payload.subject_recruitment_sources = recruitments.map((e) => ({
          recruitment_id: e.recruitment_id,
          ...(e.id ? { id: e.id } : {}),
        }));
      }

      /* Deals Logic */
      const deals = source.subject_deal_status
        ? Object.values(source.subject_deal_status).filter((d) => d.deal_id)
        : [];

      if (deals.length) {
        payload.subject_deal_status = deals.map((d) => ({
          deal_id: d.deal_id,
          status: d.status,
          ...(d.id ? { id: d.id } : {}),
        }));
      }

      Object.assign(payload, sanitizeMedicalSections(source));

      Object.keys(payload).forEach((key) => {
        if (Array.isArray(payload[key]) && payload[key].length === 0) {
          delete payload[key];
        }
      });

      const res = await upsertSubject({
        input: payload,
        input_delete:
          deleteModel.current.length > 0 ? deleteModel.current : undefined,
      });

      setIsSaving(false);
      onSave?.({ data: res });
    } catch (err) {
      console.error("SAVE FAILED:", err);
      setIsSaving(false);

      // Extract cleaner error message if possible
      let msg = err?.message || "Save failed";
      if (err.errors && err.errors.length > 0) {
        msg = err.errors[0].message;
      }
      setErrorMessage(msg);
    }
  };

  /* RENDER */
  return (
    <>
      {errorMessage && (
        <ErrorModal
          title="Error"
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <div className="container-fluid px-4">
        {isLoaded && (
          <div className="d-flex justify-content-between align-items-center mb-3 mt-3 mx-2">
            <div>{links.length > 0 && <Breadcrumb links={links} />}</div>
            {isCreate && (
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-primary"
                  disabled={isSaving}
                  onClick={handleSave}
                  style={{ backgroundColor: purple, borderColor: purple }}
                >
                  {createLabel}
                </button>

                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
        <div>{callView}</div>
      </div>
    </>
  );
}
